// CAMPO DE ESCOLHA COM VÁRIAS OPÇÕES — Cidade e Tipo da barra dos heros novos (Aurora/Horizonte/Panorama/Larissa).
//
// Daniel 21/10: a busca da home dos modelos novos marca VÁRIAS cidades, bairros e tipos
// (utils/buscaMultiSelecao). Mesmo visual do CampoSelecao vizinho e do CampoQuantidadeMulti — as
// classes do SmartLocationSearch, painel em PORTAL no <body> com a classe de tema do hero —, mas a
// lista é de MARCAR: clicar liga/desliga a opção e a lista continua aberta; a opção vazia ("Todas
// as cidades") limpa e fecha.
//
// Fechado, o campo mostra o resumo ("Torres" ou "3 cidades"); aberto, vira o filtro por digitação (as listas de
// cidade/tipo são longas). O valor é o texto de ids "12,7" que o useHeroSearch leva à URL; um id só
// é o valor de sempre. Teclado: ↑ ↓ andam, Enter marca, Esc fecha (Enter com a lista fechada segue
// submetendo o form).
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { normalizaTermo } from '../../../utils/buscaLocal';
import { alternarIdTexto, idsDoTexto, opcoesDoTexto, resumoMarcados } from '../../../utils/buscaMultiSelecao';
import type { OpcaoCampo } from './CampoSelecao';
import styles from './SmartLocationSearch.module.css';
import local from './CampoQuantidadeMulti.module.css';

export interface CampoSelecaoMultiProps {
    opcoes: OpcaoCampo[];
    /** ids marcados, "12,7" ('' = nenhum). */
    valor: string;
    onChange: (_valor: string) => void;
    /** a opção que limpa ("Todas as cidades") — também o texto do campo vazio. */
    rotuloVazio: string;
    /** resumo com 2+ marcadas: "3 cidades". */
    plural: string;
    ariaLabel: string;
    className?: string;
}

const CampoSelecaoMulti: React.FC<CampoSelecaoMultiProps> = ({ opcoes, valor, onChange, rotuloVazio, plural, ariaLabel, className }) => {
    const reactId = useId();
    const idBase = `sel-multi-${String(reactId).replace(/[^a-zA-Z0-9]/g, '')}`;
    const [aberto, setAberto] = useState(false);
    const [termo, setTermo] = useState('');
    // -1 = a opção vazia; 0.. = as opções filtradas
    const [destaque, setDestaque] = useState(-1);
    const [abreParaCima, setAbreParaCima] = useState(false);
    const [caixa, setCaixa] = useState<{ left: number; width: number; top: number; bottom: number } | null>(null);
    const [montado, setMontado] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const painelRef = useRef<HTMLDivElement>(null);
    useEffect(() => setMontado(true), []);

    const validas = useMemo(() => (opcoes || []).filter((o) => o?.label && String(o.value ?? '') !== ''), [opcoes]);
    const marcados = idsDoTexto(valor);
    const labelsMarcados = opcoesDoTexto(validas, valor).map((o) => String(o.label));
    const resumo = resumoMarcados(labelsMarcados, plural);
    const filtradas = useMemo(() => {
        const t = normalizaTermo(termo);
        return t ? validas.filter((o) => normalizaTermo(o.label).includes(t)) : validas;
    }, [validas, termo]);

    // mesma conta do SmartLocationSearch: onde o painel (fixed, no <body>) aparece e se abre para cima
    useEffect(() => {
        if (!aberto || typeof window === 'undefined') return undefined;
        const medir = () => {
            const r = wrapRef.current?.getBoundingClientRect();
            if (!r) return;
            setCaixa({ left: r.left, width: r.width, top: r.top, bottom: r.bottom });
            const abaixo = window.innerHeight - r.bottom;
            setAbreParaCima(abaixo < 320 && r.top > abaixo);
        };
        medir();
        window.addEventListener('scroll', medir, true);
        window.addEventListener('resize', medir);
        return () => {
            window.removeEventListener('scroll', medir, true);
            window.removeEventListener('resize', medir);
        };
    }, [aberto]);

    useEffect(() => {
        if (!aberto) return undefined;
        const aoClicarFora = (evento: MouseEvent) => {
            const alvo = evento.target as Node;
            if (wrapRef.current?.contains(alvo) || painelRef.current?.contains(alvo)) return;
            setAberto(false);
        };
        document.addEventListener('mousedown', aoClicarFora);
        return () => document.removeEventListener('mousedown', aoClicarFora);
    }, [aberto]);

    // a opção em destaque fica sempre à vista
    useEffect(() => {
        if (!aberto) return;
        const el = document.getElementById(`${idBase}-opt-${destaque}`);
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    }, [aberto, destaque, idBase]);

    const abrir = () => {
        if (aberto) return;
        setTermo('');
        setDestaque(-1);
        setAberto(true);
    };
    const limpar = () => {
        onChange('');
        setAberto(false);
    };
    const alternar = (id: unknown) => onChange(alternarIdTexto(valor, id));

    const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>) => {
        if (evento.key === 'Escape') {
            // com a lista aberta o Esc fecha SÓ a lista — dentro do painel "Busca avançada" ele não pode
            // chegar ao document (onde o painel escuta o Esc para fechar)
            if (aberto) evento.stopPropagation();
            setAberto(false);
            return;
        }
        if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
            evento.preventDefault();
            if (!aberto) {
                abrir();
                return;
            }
            const passo = evento.key === 'ArrowDown' ? 1 : -1;
            setDestaque((atual) => {
                const proximo = atual + passo;
                if (proximo < -1) return filtradas.length - 1;
                if (proximo >= filtradas.length) return -1;
                return proximo;
            });
            return;
        }
        if (evento.key === 'Enter' && aberto) {
            evento.preventDefault();
            if (destaque < 0) limpar();
            else if (filtradas[destaque]) alternar(filtradas[destaque].value);
        }
    };

    const conteudoPainel = (
        <ul className={styles.lista} id={`${idBase}-lista`} role="listbox" aria-label={ariaLabel} aria-multiselectable="true">
            <li
                id={`${idBase}-opt--1`}
                className={`${styles.opcao} ${styles.opcaoVazia} ${destaque === -1 ? styles.opcaoAtiva : ''}`}
                role="option"
                aria-selected={!marcados.length}
                onMouseDown={(e) => {
                    e.preventDefault();
                    limpar();
                }}
                onMouseEnter={() => setDestaque(-1)}
            >
                <span className={styles.opcaoTexto}>{rotuloVazio}</span>
            </li>
            {filtradas.map((o, i) => {
                const marcado = marcados.includes(String(o.value));
                return (
                    <li
                        key={`${o.value}-${i}`}
                        id={`${idBase}-opt-${i}`}
                        role="option"
                        aria-selected={marcado}
                        className={`${styles.opcao} ${local.opcao} ${i === destaque ? styles.opcaoAtiva : ''}`}
                        // mousedown (não click): o input não perde o foco e a lista fica aberta
                        onMouseDown={(e) => {
                            e.preventDefault();
                            setDestaque(i);
                            alternar(o.value);
                        }}
                        onMouseEnter={() => setDestaque(i)}
                    >
                        <span className={`${local.caixinha} ${marcado ? local.caixinhaMarcada : ''}`} aria-hidden="true" />
                        <span className={styles.opcaoTexto}>
                            <strong className={styles.opcaoLabel}>{o.label}</strong>
                        </span>
                    </li>
                );
            })}
            {!filtradas.length ? (
                <li className={styles.aviso} role="presentation">
                    Nada encontrado
                </li>
            ) : null}
            <li className={local.dica} role="presentation">
                Marque um ou mais
            </li>
        </ul>
    );

    return (
        <div ref={wrapRef} className={`${styles.wrap} ${className || ''}`}>
            <input
                id={idBase}
                className={styles.input}
                type="text"
                role="combobox"
                autoComplete="off"
                spellCheck={false}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={aberto}
                aria-controls={`${idBase}-lista`}
                aria-activedescendant={aberto ? `${idBase}-opt-${destaque}` : undefined}
                // aberto: o campo é o filtro e o resumo vira a dica; fechado: o resumo do que está marcado
                placeholder={aberto ? resumo || 'Digite para filtrar' : rotuloVazio}
                value={aberto ? termo : resumo}
                title={labelsMarcados.join(', ') || undefined}
                onChange={(e) => {
                    setTermo(e.target.value);
                    setDestaque(e.target.value.trim() ? 0 : -1);
                    if (!aberto) setAberto(true);
                }}
                onFocus={abrir}
                onClick={abrir}
                onBlur={() => setAberto(false)}
                onKeyDown={aoTeclar}
            />
            <span className={styles.seta} aria-hidden="true" />
            {montado && aberto && caixa
                ? createPortal(
                      <div
                          ref={painelRef}
                          className={`${styles.painel} ${styles.painelFlutuante} ${abreParaCima ? styles.painelAcima : ''} ${className || ''}`}
                          // clicar no painel (inclusive na barra de rolagem da lista longa) não tira o foco do campo
                          onMouseDown={(e) => e.preventDefault()}
                          style={{
                              left: caixa.left - 14,
                              width: Math.max(caixa.width + 28, 240),
                              ...(abreParaCima ? { bottom: window.innerHeight - caixa.top + 10 } : { top: caixa.bottom + 10 }),
                          }}
                      >
                          {conteudoPainel}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};

export default CampoSelecaoMulti;
